// Copyright 2026. WebPros International GmbH. All rights reserved.

import * as React from 'react';
import { connect } from 'react-redux';
import {
    bindActionCreators,
    Dispatch,
} from 'redux';
import { RootState } from 'admin/core/store';
import { Loader } from 'common/components';
import { LOADING_FLAGS } from 'common/modules/app/loadingFlags/constants';
import * as failoverDomainActions from 'common/modules/failoverDomain/actions';
import { Translate } from '@plesk/ui-library';
import { PageHeader } from 'admin/common/components/PageHeader/PageHeader';
import { StyledTable } from 'common/components/styles/StyledTable';
import { IFailoverDomainResponse } from 'common/api/resources/FailoverDomain';
import FailoverDomainTable from 'admin/failoverDomain/containers/FailoverDomainTable';
import FailoverDomainCreateDialog from 'admin/failoverDomain/containers/FailoverDomainCreateDialog/FailoverDomainCreateDialog';
import { track } from 'common/analytics/mixpanel';
import { ANALYTICS_EVENTS } from 'common/analytics/constants';
import { ICONS } from 'common/constants';
import useQuery from 'common/hooks/useQuery';
import { useRequestCancellationEffect } from 'common/hooks/useRequestCancellationEffect';
import HighAvailabilityDisabledAlert from 'admin/highAvailability/components/HighAvailabilityDisabledAlert/HighAvailabilityDisabledAlert';
import { isHighAvailabilityCrDisabled } from 'common/modules/settings/selectors';
import { reloadListData } from 'common/helpers/list';

interface IFailoverDomainListFilters {
    is_active?: string;
}

export type FailoverDomainsProps =
    ReturnType<typeof mapStateToProps> &
    ReturnType<typeof mapDispatchToProps>;

export const FailoverDomains: React.FC<FailoverDomainsProps> = ({
    list,
    isLoadingList,
    isHaGloballyDisabled,
    failoverDomainActions: {
        getFailoverDomains,
        toggleActiveState,
        deleteFailoverDomain,
    },
}) => {
    const [dialogOpen, setDialogOpen] = React.useState(false);
    const [editItem, setEditItem] = React.useState<IFailoverDomainResponse | undefined>(undefined);
    const [query] = useQuery<IFailoverDomainListFilters>();

    React.useEffect(() => {
        track(ANALYTICS_EVENTS.ADMIN.FAILOVER_DOMAINS.VISIT);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const filters = query.is_active !== undefined ? { is_active: query.is_active } : undefined;

    const loadPaginated = (page: number) => getFailoverDomains({ page, filters });

    const isFirstLoading = useRequestCancellationEffect(
        token => getFailoverDomains({ filters }, token),
        [query.is_active]
    );

    const handleCreate = () => {
        setEditItem(undefined);
        setDialogOpen(true);
    };

    const handleEdit = (item: IFailoverDomainResponse) => {
        setEditItem(item);
        setDialogOpen(true);
    };

    const handleDialogClose = () => {
        setDialogOpen(false);
        setEditItem(undefined);
    };

    const handleDialogSaved = () => {
        setDialogOpen(false);
        setEditItem(undefined);
        loadPaginated(list.meta.current_page);
    };

    const handleToggleActive = (item: IFailoverDomainResponse) => {
        toggleActiveState(item.id, {
            is_active: !item.is_active,
        });
    };

    const handleDelete = async (item: IFailoverDomainResponse) => {
        if (await deleteFailoverDomain(item.id)) {
            reloadListData(list, loadPaginated);
        }
    };

    return (
        <>
            <PageHeader
                title={<Translate content="failoverDomain.title" />}
                buttonText="failoverDomain.addBtn"
                buttonIcon={ICONS.RESOURCE}
                onButtonClick={handleCreate}
                isButtonShown={list.data.length > 0}
            />
            {isHaGloballyDisabled && <HighAvailabilityDisabledAlert />}
            <StyledTable>
                <Loader isLoading={isFirstLoading}>
                    <FailoverDomainTable
                        items={list.data}
                        meta={list.meta}
                        loadItems={loadPaginated}
                        isLoading={isLoadingList}
                        isFirstLoading={isFirstLoading}
                        onCreate={handleCreate}
                        onEdit={handleEdit}
                        onToggleActive={handleToggleActive}
                        onDelete={handleDelete}
                    />
                </Loader>
            </StyledTable>
            {dialogOpen && (
                <FailoverDomainCreateDialog
                    editItem={editItem}
                    onClose={handleDialogClose}
                    onSaved={handleDialogSaved}
                />
            )}
        </>
    );
};

const mapStateToProps = (state: RootState) => ({
    list: state.failoverDomain.list,
    isLoadingList: state.app.loadingFlags.has(LOADING_FLAGS.FAILOVER_DOMAIN_LIST),
    isHaGloballyDisabled: isHighAvailabilityCrDisabled(state),
});

const mapDispatchToProps = (dispatch: Dispatch) => ({
    failoverDomainActions: bindActionCreators(failoverDomainActions, dispatch),
});

export default connect(mapStateToProps, mapDispatchToProps)(FailoverDomains);
